import { enforce, omitWhen, test } from 'vest';

import {
  isEndpointUrl,
  isTimeZone,
  withDefaults,
} from 'admin-panel/utils/integrations/config';

import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';
import type {
  IntegrationFormValues,
  ServiceFormDefinition,
} from 'admin-panel/utils/integrations/types';

export interface UnifocusService {
  apiEndpointUrl: string;
  propertyId: string;
  publicKey: string;
  privateKey: string;
  applicationId: string;
  timezone: string;
  fulfillmentAdvanceMinutes: number | null;
}

const SERVICE = 'currentConfig.service';

const defaultConfig = (): IntegrationConfig => ({
  service: {
    apiEndpointUrl: '',
    propertyId: '',
    publicKey: '',
    privateKey: '',
    applicationId: '',
    timezone: '',
    fulfillmentAdvanceMinutes: null,
  },
  features: {},
  cron: [],
  webhooks: [],
});

export const unifocus: ServiceFormDefinition = {
  toFormConfig: (config) => withDefaults(defaultConfig(), config),

  validate(values: IntegrationFormValues, t) {
    const service = values.currentConfig.service as unknown as UnifocusService;

    test(`${SERVICE}.apiEndpointUrl`, t('validations.required'), () => {
      enforce(service.apiEndpointUrl).isNotBlank();
    });

    omitWhen(!service.apiEndpointUrl, () => {
      test(`${SERVICE}.apiEndpointUrl`, t('validations.url'), () => {
        enforce(isEndpointUrl(service.apiEndpointUrl)).isTruthy();
      });
    });

    for (const field of ['propertyId', 'publicKey', 'privateKey'] as const) {
      test(`${SERVICE}.${field}`, t('validations.required'), () => {
        enforce(service[field]).isNotBlank();
      });
    }

    test(`${SERVICE}.timezone`, t('validations.required'), () => {
      enforce(service.timezone).isNotBlank();
    });

    omitWhen(!service.timezone, () => {
      test(`${SERVICE}.timezone`, t('validations.invalid'), () => {
        enforce(isTimeZone(service.timezone)).isTruthy();
      });
    });

    omitWhen(service.fulfillmentAdvanceMinutes === null, () => {
      test(
        `${SERVICE}.fulfillmentAdvanceMinutes`,
        t('validations.invalid'),
        () => {
          enforce(
            Number.isInteger(service.fulfillmentAdvanceMinutes) &&
              Number(service.fulfillmentAdvanceMinutes) >= 0
          ).isTruthy();
        }
      );
    });
  },
};
